/*
 * Звуки приложения: уведомление, новое сообщение, входящий звонок и гудки
 * дозванивания. Все четыре синтезированы для CODE ERP из одного мотива и
 * играются одним инструментом (хандпан), поэтому слышатся как одна семья.
 *
 * Браузер не даёт играть звук, пока человек ни разу не тронул страницу, —
 * поэтому любой play() может отвалиться. Это нормальный сценарий, а не сбой:
 * до первого клика человек всё равно не ждёт от вкладки звуков.
 */

import { claimOnce } from './tabs';

const FILES = {
  notify: '/sounds/notify.wav',
  message: '/sounds/message.wav',
  hand: '/sounds/hand.wav',
  incoming: '/sounds/incoming-call.wav',
  outgoing: '/sounds/outgoing-call.wav',
} as const;

type Sound = keyof typeof FILES;

/** По одному элементу на звук: повторный вызов перезапускает, а не наслаивает. */
const players = new Map<Sound, HTMLAudioElement>();
const lastPlayed = new Map<Sound, number>();

function player(name: Sound): HTMLAudioElement {
  let audio = players.get(name);
  if (!audio) {
    audio = new Audio(FILES[name]);
    audio.preload = 'auto';
    players.set(name, audio);
  }
  return audio;
}

/**
 * Короткий звук. Повторы в пределах трети секунды глушим: колокольчик в шапке
 * висит в двух экземплярах (для широкого экрана и для узкого), и оба зовут
 * звук на одно и то же событие.
 */
export function playSound(name: 'notify' | 'message' | 'hand'): void {
  const now = Date.now();
  if (now - (lastPlayed.get(name) ?? 0) < 300) return;
  lastPlayed.set(name, now);
  const audio = player(name);
  audio.currentTime = 0;
  void audio.play().catch(() => {});
}

/**
 * Звук, который звучит один раз на всё приложение, сколько бы вкладок ни было
 * открыто (жалоба Ильи 21.09.2026: сообщение звенело в каждой вкладке).
 * `key` — то, про что звук: номер сообщения, номер уведомления. Первая
 * успевшая вкладка оставляет отметку в памяти браузера, остальные молчат.
 *
 * Памяти нет (частное окно, запрет на хранилище) — играем как раньше: лучше
 * два сигнала, чем ни одного.
 */
export function playSoundOnce(name: 'notify' | 'message' | 'hand', key: string): void {
  if (!claimOnce(`${name}:${key}`)) return;
  playSound(name);
}

/*
 * Входящий звонок звенит в одной вкладке, а не в каждой (спека
 * 2026-10-02-tabs-devices-sync, раздел 2). Звенит та, что взяла замок
 * браузера. Браузер не дал ей звучать (вкладку ни разу не трогали) —
 * она отдаёт замок следующей; звенящую закрыли — замок переходит сам.
 * Замков нет — звенит каждая, как раньше.
 */
const RING_LOCK = 'code-ring';
let ringStop: AbortController | null = null;
let ringRelease: (() => void) | null = null;

/**
 * Сколько звеневшая вкладка держит замок молча после своего стопа. Ответили
 * или отклонили здесь — соседняя вкладка узнает об этом от сервера через
 * долю секунды, а отпусти замок сразу, она успела бы чирикнуть. Закрытая
 * вкладка отпускает замок сразу — это делает браузер.
 */
export const RING_HOLD_MS = 1500;

function startRing(): void {
  if (ringStop) return;
  const stop = new AbortController();
  ringStop = stop;
  navigator.locks
    .request(RING_LOCK, { signal: stop.signal }, async () => {
      if (stop.signal.aborted) return;
      const audio = player('incoming');
      audio.loop = true;
      audio.currentTime = 0;
      const played = await audio.play().then(
        () => true,
        () => false,
      );
      // не дали звучать — замок сразу следующей вкладке
      if (!played) return;
      if (stop.signal.aborted) {
        audio.pause();
      } else {
        await new Promise<void>((resolve) => {
          ringRelease = resolve;
        });
      }
      // звук уже стих; новый звонок в этой же вкладке подождёт конца паузы
      await new Promise<void>((resolve) => setTimeout(resolve, RING_HOLD_MS));
    })
    // отменили, пока ждали своей очереди
    .catch(() => {});
}

/** Звонок и гудки играют в петле, пока их не остановят. */
export function startLoop(name: 'incoming' | 'outgoing'): void {
  if (name === 'incoming' && typeof navigator !== 'undefined' && navigator.locks) {
    startRing();
    return;
  }
  const audio = player(name);
  audio.loop = true;
  if (!audio.paused) return;
  audio.currentTime = 0;
  void audio.play().catch(() => {});
}

export function stopLoops(): void {
  ringStop?.abort();
  ringStop = null;
  ringRelease?.();
  ringRelease = null;
  for (const name of ['incoming', 'outgoing'] as const) {
    const audio = players.get(name);
    if (!audio) continue;
    audio.pause();
    audio.currentTime = 0;
  }
}
